import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Popover,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNotification } from "../../contexts/NotificationContext";
import { useAddIdeaBookItem } from "../../hooks/idea-books/use-add-idea-book-item";
import { useCreateIdeaBook } from "../../hooks/idea-books/use-create-idea-book";
import { useGetIdeaBooks } from "../../hooks/idea-books/use-get-idea-books";
import type { IdeaBook } from "../../hooks/idea-books/types";

type SaveToIdeaBookPopoverProps = {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  homeownerId?: string | null;
  imageId?: string | null;
  imageIds?: (string | null | undefined)[] | null;
  imageUrl?: string | null;
};

export const SaveToIdeaBookPopover = ({
  open,
  anchorEl,
  onClose,
  homeownerId,
  imageId,
  imageIds,
  imageUrl,
}: SaveToIdeaBookPopoverProps) => {
  const { showError, showSuccess } = useNotification();
  const { getIdeaBooks } = useGetIdeaBooks();
  const { createIdeaBook } = useCreateIdeaBook();
  const { addIdeaBookItem } = useAddIdeaBookItem();
  const [ideaBooks, setIdeaBooks] = useState<IdeaBook[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [newBookName, setNewBookName] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const loadIdeaBooks = useCallback(async () => {
    if (!homeownerId) return;
    setIsLoading(true);
    try {
      const result = await getIdeaBooks(homeownerId, true);
      setIdeaBooks(result);
    } catch (error) {
      showError("Unable to load idea books. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [getIdeaBooks, homeownerId, showError]);

  useEffect(() => {
    if (!open) return;
    setSearchValue("");
    setIsCreating(false);
    setNewBookName("");
    loadIdeaBooks();
  }, [open, loadIdeaBooks]);

  const filteredBooks = useMemo(() => {
    const query = searchValue.trim().toLowerCase();
    if (!query) return ideaBooks;
    return ideaBooks.filter((book) =>
      book.name.toLowerCase().includes(query)
    );
  }, [ideaBooks, searchValue]);

  const selectedImageIds = useMemo(() => {
    const ids = new Set<string>();
    if (imageId) {
      ids.add(imageId);
    }
    (imageIds ?? []).forEach((id) => {
      if (!id) return;
      ids.add(String(id));
    });
    return Array.from(ids);
  }, [imageId, imageIds]);

  const handleSaveToBook = async (bookId: string) => {
    if (!homeownerId || selectedImageIds.length === 0) {
      showError("Unable to save these images right now.");
      return;
    }

    setIsSaving(true);
    try {
      await Promise.all(
        selectedImageIds.map((selectedId) =>
          addIdeaBookItem({
            ideaBookId: bookId,
            homeownerId,
            imageId: selectedId,
          })
        )
      );
      showSuccess("Saved to idea book!");
      await loadIdeaBooks();
      onClose();
    } catch (error) {
      showError("Unable to save to idea book. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreateIdeaBook = async () => {
    const trimmedName = newBookName.trim();
    if (!trimmedName) {
      showError("Please enter an idea book name.");
      return;
    }
    if (!homeownerId || selectedImageIds.length === 0) {
      showError("Unable to create idea book right now.");
      return;
    }

    setIsSaving(true);
    try {
      const newBook = await createIdeaBook({
        homeownerId,
        name: trimmedName,
      });
      if (newBook?._id) {
        await Promise.all(
          selectedImageIds.map((selectedId) =>
            addIdeaBookItem({
              ideaBookId: newBook._id,
              homeownerId,
              imageId: selectedId,
            })
          )
        );
      }
      showSuccess("Idea book created!");
      setNewBookName("");
      setIsCreating(false);
      await loadIdeaBooks();
      onClose();
    } catch (error) {
      showError("Unable to create idea book. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      PaperProps={{
        sx: {
          width: 320,
          borderRadius: "16px",
          boxShadow: "0 12px 30px rgba(0, 0, 0, 0.12)",
          p: 2,
        },
      }}
    >
      <Typography
        variant="subtitle1"
        sx={{ fontWeight: 700, textAlign: "center", mb: 2 }}
      >
        Save to Book
      </Typography>

      <TextField
        placeholder="Search"
        size="small"
        fullWidth
        value={searchValue}
        onChange={(event) => setSearchValue(event.target.value)}
        InputProps={{
          startAdornment: <SearchIcon sx={{ color: "#9CA3AF", mr: 1 }} />,
        }}
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: "12px",
            backgroundColor: "#F9FAFB",
          },
        }}
      />

      <Box
        sx={{
          mt: 2,
          maxHeight: 260,
          overflowY: "auto",
          pr: 0.5,
        }}
      >
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              py: 3,
            }}
          >
            <CircularProgress size={24} />
          </Box>
        ) : filteredBooks.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: "center", py: 2 }}
          >
            No idea books yet.
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {filteredBooks.map((book) => {
              const coverUrl =
                book.coverImage?.url ?? book.coverImage?.path ?? "";

              return (
                <Box
                  key={book._id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    border: "1px dashed #E5E7EB",
                    borderRadius: "12px",
                    px: 1.5,
                    py: 1.25,
                    backgroundColor: "#fff",
                    "&:hover": {
                      backgroundColor: "#F9FAFB",
                    },
                    "&:hover .idea-book-add": {
                      opacity: 1,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: "10px",
                      overflow: "hidden",
                      backgroundColor: "#F3F4F6",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {coverUrl ? (
                      <Box
                        component="img"
                        src={coverUrl}
                        alt={book.name}
                        sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    ) : (
                      <ImageOutlinedIcon sx={{ color: "#9CA3AF" }} />
                    )}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        textDecoration: "underline",
                        color: "#111827",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {book.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {book.itemCount} ideas
                    </Typography>
                  </Box>
                  <IconButton
                    className="idea-book-add"
                    onClick={() => handleSaveToBook(book._id)}
                    disabled={isSaving}
                    sx={{
                      opacity: 0,
                      transition: "opacity 0.2s ease",
                    }}
                  >
                    {isSaving ? (
                      <CircularProgress size={18} />
                    ) : (
                      <AddIcon />
                    )}
                  </IconButton>
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>

      <Divider sx={{ my: 2 }} />

      <Stack spacing={1.5}>
        {!isCreating && (
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setIsCreating(true)}
            fullWidth
            sx={{
              borderRadius: "12px",
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Create Idea Book
          </Button>
        )}

        {isCreating && (
          <Stack spacing={1}>
            <TextField
              size="small"
              placeholder="Idea Book Name"
              value={newBookName}
              onChange={(event) => setNewBookName(event.target.value)}
              autoFocus
            />
            <Button
              variant="contained"
              onClick={handleCreateIdeaBook}
              disabled={isSaving}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              {isSaving ? "Saving..." : "Create & Save"}
            </Button>
          </Stack>
        )}
      </Stack>

      {imageUrl && (
        <Box sx={{ mt: 2, display: "none" }} aria-hidden="true">
          {imageUrl}
        </Box>
      )}
    </Popover>
  );
};
